import React from 'react';
import { connect } from 'react-redux';
import { createSelector } from '@reduxjs/toolkit';
import { publicAssets } from '@ff-os/pdp-shared-redux/apis/publicAssets';
import { selectLanguage } from '@ff-os/pdp-shared-redux/slices/globalSlice';
import { AspectRatio } from '@ff-os/shared-react/components/aspect-ratio/AspectRatio';
import { Card } from '@ff-os/shared-react/components/card/Card';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid, GridLayout } from '@ff-os/shared-react/components/grid/Grid';
import { Spinner } from '@ff-os/shared-react/components/loaders/spinner/Spinner';
import { forWeb } from '@ff-os/shared-utils/asset-urls';
import { selectAspectRatioByVariableId } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpDispatch, GiftPdpState } from '@ff-scene-plugins/gift-redux/store';
import { noop } from 'lodash';
import { selectAssetUrlById, selectCollectionIdById, selectIsOptionalById, updateAssetUrlByIds } from '../_redux';
import { EngravingInputRemoveSelection } from './EngravingInputRemoveSelection';
import styles from './EngravingInputOptionGrid.module.scss';

export const _EngravingInputOptionGrid = ({
    variableIds, collectionId,
    selectedAssetUrl, isOptional,
    language, gridLayout,
    commit
}: Props) => {

    const urls = publicAssets.useGetUrlsInCollectionQuery(collectionId);

    return (
        <Grid gap='xs' {...gridLayout} wrap>

            {isOptional && (
                <Cell>
                    <EngravingInputRemoveSelection
                        variableIds={variableIds}
                        active={!urls.isLoading && !selectedAssetUrl} />
                </Cell>
            )}

            {urls.isLoading && (
                <Cell>
                    <Card onClick={noop}>
                        <AspectRatio>
                            <Spinner overlaySize='full' spinnerSize='small' />
                        </AspectRatio>
                    </Card>
                </Cell>
            )}

            {urls.isSuccess && urls.data?.map((url: string) => (
                <Cell key={url}>
                    <Card onClick={() => commit(url)} active={url === selectedAssetUrl}>
                        <img crossOrigin='anonymous' loading='lazy'
                            width={300} height={300}
                            src={forWeb(url, { w: 300, h: 300, language })}
                            className={styles.image} alt='' />
                    </Card>
                </Cell>
            ))}
        </Grid>
    );
};

type Props = {
    variableIds: string[];
    collectionId: string;
    selectedAssetUrl: string;
    isOptional: boolean;
    commit: (url: string) => void;
    gridLayout: GridLayout;
    language: string;
};

const mapStateToProps = (state: GiftPdpState, ownProps: { variableIds: string[] }) => ({
    collectionId: selectCollectionIdById(state, ownProps.variableIds[0])!,
    isOptional: selectIsOptionalById(state, ownProps.variableIds[0])!,
    selectedAssetUrl: selectAssetUrlById(state, ownProps.variableIds[0])!,
    gridLayout: selectLayout(state, ownProps.variableIds[0]),
    language: selectLanguage(state)
});

const mapDispatchToProps = (dispatch: GiftPdpDispatch, ownProps: { variableIds: string[] }) => ({
    commit: (svgUrl: string) => dispatch(updateAssetUrlByIds(ownProps.variableIds, svgUrl))
});

export const EngravingInputOptionGrid = connect(
    mapStateToProps,
    mapDispatchToProps
)(_EngravingInputOptionGrid);

const selectLayout = createSelector(
    (state: GiftPdpState, id: string) => selectAspectRatioByVariableId(state, id),
    (aspectRatio): GridLayout => {
        if (aspectRatio && aspectRatio > 1.5) {
            return {
                xs: 'full',
                md: '1/2'
            };
        } else {
            return {
                xs: '1/4',
                md: '1/5'
            };
        }
    });